import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SearchResult, SearchQuery, PaginatedResponse, ApiError } from '../../types';
import { RootState } from '@store/index';

interface SearchState {
  results: SearchResult[];
  query: string;
  total: number;
  page: number;
  pageSize: number;
  facets: Record<string, Array<{ value: string; count: number }>>;
  recentSearches: string[];
  isLoading: boolean;
  error: string | null;
}

const initialState: SearchState = {
  results: [],
  query: '',
  total: 0,
  page: 1,
  pageSize: 20,
  facets: {},
  recentSearches: [],
  isLoading: false,
  error: null,
};

// Async thunks for search operations
export const performSearch = createAsyncThunk<
  PaginatedResponse<SearchResult> & {
    facets: Record<string, Array<{ value: string; count: number }>>;
    searchTime?: number;
  },
  SearchQuery,
  { rejectValue: ApiError }
>('search/performSearch', async (searchQuery, { rejectWithValue }) => {
  try {
    // Import api here to avoid circular dependency
    const { api } = await import('../../services/api');

    const response = await api.post<PaginatedResponse<SearchResult> & {
      facets: Record<string, Array<{ value: string; count: number }>>;
      searchTime?: number;
    }>('/search', searchQuery);

    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

export const getSearchSuggestions = createAsyncThunk<
  { suggestions: Array<{ text: string; type: string; score: number }> },
  { query: string; limit?: number },
  { rejectValue: ApiError }
>('search/getSearchSuggestions', async ({ query, limit = 10 }, { rejectWithValue }) => {
  try {
    const { api } = await import('../../services/api');

    const response = await api.get<{ suggestions: Array<{ text: string; type: string; score: number }> }>(
      `/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`
    );

    return response;
  } catch (error: any) {
    return rejectWithValue(error);
  }
});

// Slice
const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery: (state, action: PayloadAction<string>) => {
      state.query = action.payload;
    },
    addRecentSearch: (state, action: PayloadAction<string>) => {
      const search = action.payload.trim();
      if (search && !state.recentSearches.includes(search)) {
        state.recentSearches = [search, ...state.recentSearches.slice(0, 9)];
      }
    },
    clearSearchResults: (state) => {
      state.results = [];
      state.total = 0;
      state.facets = {};
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(performSearch.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(performSearch.fulfilled, (state, action) => {
        state.isLoading = false;
        state.results = action.payload.results;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.facets = action.payload.facets;
      })
      .addCase(performSearch.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || 'Search failed';
      });
  },
});

// Actions
export const { setQuery, addRecentSearch, clearSearchResults, clearError } = searchSlice.actions;

// Selectors
export const selectSearchResults = (state: RootState) => state.search.results;
export const selectSearchQuery = (state: RootState) => state.search.query;
export const selectSearchFacets = (state: RootState) => state.search.facets;
export const selectRecentSearches = (state: RootState) => state.search.recentSearches;
export const selectSearchLoading = (state: RootState) => state.search.isLoading;
export const selectSearchError = (state: RootState) => state.search.error;

export default searchSlice.reducer;
